<template>
  <div class="solar-plant-batteries">
    <div v-if="!batteries || batteries.length === 0" class="text-muted text-center py-3">
      <i class="fa fa-battery-three-quarters fa-2x mb-2 d-block"></i>
      {{ $t('no_batteries_added') }}
    </div>
    <div v-else>
      <b-row
        v-for="(battery, index) in batteries"
        :key="getBatteryKey(battery, index)"
        class="battery-row mb-2"
      >
        <b-col lg="10" md="6" sm="12">
          <div class="d-flex align-items-center">
            <div class="battery-info flex-grow-1">
              <router-link
                v-if="battery.item && battery.item.number"
                target="_blank"
                :to="{
                  name: 'ItemEditPage',
                  params: { itemNumber: battery.item.number },
                }"
                class="mr-2"
              >
                #{{ battery.item.number.toString().padStart(4, '0') }}
                <i class="fa fa-sm fa-external-link"></i>
              </router-link>
              <span v-if="getManufacturer(battery.item)">
                {{ getManufacturer(battery.item) }} - {{ getType(battery.item) }}
              </span>
              <span v-else class="text-muted">-</span>
              <span v-if="getCapacity(battery.item)" class="ml-2 text-muted">
                ({{ getCapacity(battery.item) }} kWh)
              </span>
            </div>
            <div class="quantity-controls d-flex align-items-center ml-3">
              <button
                class="btn btn-sm btn-danger"
                @click="decreaseCount(index)"
                :disabled="battery.count <= 1"
              >
                <i class="fa fa-minus"></i>
              </button>
              <input
                v-model.number="battery.count"
                type="number"
                min="1"
                class="form-control form-control-sm mx-1 quantity-input"
                @input="updateCount(index, $event.target.value)"
              />
              <button class="btn btn-sm btn-success" @click="increaseCount(index)">
                <i class="fa fa-plus"></i>
              </button>
            </div>
            <button
              class="btn btn-sm btn-danger ml-2"
              @click="removeBattery(index)"
              :title="$t('remove')"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1">
            <span v-if="formatBatteryTotalCapacity(battery)">{{
              formatBatteryTotalCapacity(battery)
            }}</span>
            <span v-else class="text-muted">-</span>
          </div>
        </b-col>
      </b-row>

      <!-- Total Capacity Row -->
      <b-row class="mt-3">
        <b-col lg="10" md="6" sm="12">
          <div class="text-right py-1">
            <strong>{{ $t('battery') }} {{ $t('totalCapacity') }}</strong>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1 bg-light">
            <strong>{{ totalCapacity }} kWh</strong>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  batteryCapacity,
  formatBatteriesTotalCapacity,
  formatSolarPlantTotalBatteryCapacity,
} from '@/helpers/solarPlant';

export default {
  name: 'SolarPlantBatteries',
  props: {
    batteries: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    totalCount() {
      return this.batteries.reduce((total, battery) => total + parseInt(battery.count || 0, 10), 0);
    },
    totalCapacity() {
      return formatSolarPlantTotalBatteryCapacity(this.batteries);
    },
  },
  methods: {
    getBatteryKey(battery, index) {
      return battery.item?._id || battery.item || `battery-${index}`;
    },
    getManufacturer(item) {
      return item?.battery?.manufacturer || '-';
    },
    getType(item) {
      return item?.battery?.type || '-';
    },
    getCapacity: batteryCapacity,
    formatBatteryTotalCapacity: formatBatteriesTotalCapacity,
    increaseCount(index) {
      const updatedBatteries = [...this.batteries];
      updatedBatteries[index] = {
        ...updatedBatteries[index],
        count: parseInt(updatedBatteries[index].count || 0, 10) + 1,
      };
      this.$emit('update-batteries', updatedBatteries);
    },
    decreaseCount(index) {
      const updatedBatteries = [...this.batteries];
      const currentCount = parseInt(updatedBatteries[index].count || 0, 10);
      if (currentCount > 1) {
        updatedBatteries[index] = {
          ...updatedBatteries[index],
          count: currentCount - 1,
        };
        this.$emit('update-batteries', updatedBatteries);
      }
    },
    updateCount(index, value) {
      const count = parseInt(value, 10);
      if (count >= 1) {
        const updatedBatteries = [...this.batteries];
        updatedBatteries[index] = {
          ...updatedBatteries[index],
          count,
        };
        this.$emit('update-batteries', updatedBatteries);
      }
    },
    removeBattery(index) {
      const updatedBatteries = this.batteries.filter((_, i) => i !== index);
      this.$emit('update-batteries', updatedBatteries);
    },
  },
};
</script>

<style scoped lang="scss">
.quantity-input {
  width: 60px;
  text-align: center;
}

.quantity-controls {
  gap: 4px;
}

.battery-row {
  padding: 0.25rem 0;

  &:hover {
    background-color: #f8f9fa;
  }
}

.battery-info {
  min-height: 32px;
  display: flex;
  align-items: center;
}
</style>
